import React, { useEffect, useState } from "react";
import ButtonPrimary from "componentsTemplate/Button/ButtonPrimary";
import ButtonSecondary from "componentsTemplate/Button/ButtonSecondary";
import Badge from "componentsTemplate/Badge/Badge";
import Layout from "./layout";
import LayoutDashboard from "./layout/layout";
import PerfilService from "../../services/PerfilService";
import { toast } from "react-toastify";
import { format, differenceInDays, parseISO, isAfter } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CheckIcon, XMarkIcon, MapPinIcon, CalendarIcon, CreditCardIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";

interface Access {
  id: number;
  expires_at: string;
  cancelled_at?: string;
  is_active: boolean;
  price: number;
  duration_in_days: number;
  number_of_cities: number;
  allows_all_cities: boolean;
  allows_special_categories: boolean;
  allows_contacts: boolean;
  allows_partnerships: boolean;
  allows_tips: boolean;
  allows_social_partnerships: boolean;
  allows_events: boolean;
  allows_classifieds: boolean;
  plan: {
    id: number;
    name: string;
    description: string;
    price: number;
    is_free_trial: boolean;
  };
  cities: Array<{
    id: number;
    name: string;
  }>;
  payment?: {
    payment_type: string;
  };
}

const MyPlans = () => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({} as any);
  const [accesses, setAccesses] = useState<Access[]>([]);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState<number | null>(null);

  const getUser = async () => {
    setLoading(true);
    await PerfilService
      .getUser()
      .then((response: any) => {
        setUser(response.data.user);
        setAccesses(response.data.user.accesses || []);
      })
      .catch((error: any) => {
        console.error(error);
        toast.error("Erro ao carregar dados do usuário");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getDaysRemaining = (expiresAt: string): number => {
    const expirationDate = parseISO(expiresAt);
    const today = new Date();
    return differenceInDays(expirationDate, today);
  };

  const isActivePlan = (access: Access): boolean => {
    return access.is_active && isAfter(parseISO(access.expires_at), new Date());
  };

  const getStatusBadge = (access: Access) => {
    if (access.canceled_at) {
      return <Badge name="Cancelado" color="red" />;
    }
    if (isActivePlan(access)) {
      return <Badge name="Ativo" color="green" />;
    }
    return <Badge name="Expirado" color="gray" />;
  };

  const getPaymentMethodText = (paymentType?: string): string => {
    switch (paymentType) {
      case 'credit_card':
        return 'Cartão de Crédito';
      case 'boleto':
        return 'Boleto';
      case 'pix':
        return 'PIX';
      default:
        return 'Não identificado';
    }
  };

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const getPrivileges = (access: Access) => {
    const privileges = [];
    if (access.allows_contacts) privileges.push('Contatos');
    if (access.allows_partnerships) privileges.push('Parcerias');
    if (access.allows_tips) privileges.push('Dicas');
    if (access.allows_social_partnerships) privileges.push('Parcerias Solidárias');
    if (access.allows_events) privileges.push('Eventos');
    if (access.allows_classifieds) privileges.push('Classificados');
    if (access.allows_special_categories) privileges.push('Categorias Especiais');
    return privileges;
  };

  // Função para agrupar todos os acessos por plano
  const getAccessesByPlan = (accesses: Access[]) => {
    const planMap = new Map<number, { plan: Access['plan'], accesses: Access[], latestAccess: Access }>();
    
    accesses.forEach(access => {
      const planId = access.plan.id;
      const existingGroup = planMap.get(planId);
      
      if (!existingGroup) {
        planMap.set(planId, {
          plan: access.plan,
          accesses: [access],
          latestAccess: access
        });
      } else {
        existingGroup.accesses.push(access);
        if (access.id > existingGroup.latestAccess.id) {
          existingGroup.latestAccess = access;
        }
      }
    });
    
    // Ordenar os acessos dentro de cada grupo por data de criação (mais recente primeiro)
    planMap.forEach(group => {
      group.accesses.sort((a, b) => b.id - a.id);
    });
    
    return Array.from(planMap.values());
  };

  const handleShowDetails = (planId: number) => {
    setSelectedPlanDetails(planId);
  };

  const handleBackToPlans = () => {
    setSelectedPlanDetails(null);
  };

  useEffect(() => {
    getUser();
  }, []);

  if (loading) {
    return (
      <Layout user={user}>
        <LayoutDashboard>
          <div className="rounded-xl md:border md:border-neutral-100 dark:border-neutral-800 md:p-6">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded mb-6"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                ))}
              </div>
            </div>
          </div>
        </LayoutDashboard>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <LayoutDashboard>
        <div className="rounded-xl md:border md:border-neutral-100 dark:border-neutral-800 md:p-6">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Meus Planos
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Gerencie seus planos de acesso e acompanhe os benefícios disponíveis
            </p>
          </div>

          {getAccessesByPlan(accesses).filter(group => {
            // Filtra planos free-trial inativos
            const isActive = isActivePlan(group.latestAccess);
            if (group.plan.is_free_trial && !isActive) {
              return false;
            }
            return true;
          }).length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                <CreditCardIcon className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Nenhum plano encontrado
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                Você ainda não possui nenhum plano de acesso.
              </p>
              <ButtonPrimary href="/planos">
                Explorar Planos
              </ButtonPrimary>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {getAccessesByPlan(accesses).filter(group => {
                // Filtra planos free-trial inativos
                const isActive = isActivePlan(group.latestAccess);
                if (group.plan.is_free_trial && !isActive) {
                  return false;
                }
                return true;
              }).map((group) => {
                const access = group.latestAccess;
                const daysRemaining = getDaysRemaining(access.expires_at);
                const isActive = isActivePlan(access);
                const privileges = getPrivileges(access);
                const showingDetails = selectedPlanDetails === group.plan.id;

                return (
                  <div
                    key={group.plan.id}
                    className={`relative bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 transition-all duration-200 hover:shadow-xl h-[800px] flex flex-col ${
                      isActive
                        ? 'border-green-200 dark:border-green-800'
                        : 'border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    {showingDetails ? (
                      // Vista de detalhes com histórico completo
                      <>
                        {/* Header fixo */}
                        <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
                          <div className="flex items-center justify-between mb-4">
                            <button
                              onClick={handleBackToPlans}
                              className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                            >
                              <ArrowLeftIcon className="w-4 h-4 mr-1" />
                              Voltar
                            </button>
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                              {group.plan.name}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {group.plan.description}
                            </p>
                          </div>
                        </div>

                        {/* Conteúdo com scroll */}
                        <div className="flex-1 overflow-y-auto p-1">
                          <div className="space-y-6">
                            <div>
                              <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
                                Detalhes
                              </h4>
                              
                              <div className="space-y-4">
                                {group.accesses.map((accessItem, index) => {
                                  return (
                                    <div key={accessItem.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-1">
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                                            Compra #{accessItem.id}
                                          </span>
                                          {getStatusBadge(accessItem)}
                                          {index === 0 && (
                                            <Badge name="Atual" color="blue" />
                                          )}
                                        </div>
                                      </div>
                                      
                                      <div className="grid grid-cols-2 gap-3 text-sm mb-2">
                                        <div>
                                          <span className="text-gray-500 dark:text-gray-400">Preço:</span>
                                          <div className="font-medium text-gray-900 dark:text-white">
                                            {formatPrice(accessItem.price || group.plan.price)}
                                          </div>
                                        </div>
                                        
                                        <div>
                                          <span className="text-gray-500 dark:text-gray-400">Expira:</span>
                                          <div className="font-medium text-gray-900 dark:text-white">
                                            {format(parseISO(accessItem.expires_at), "dd/MM/yyyy", { locale: ptBR })}
                                          </div>
                                        </div>
                                        
                                        <div>
                                          <span className="text-gray-500 dark:text-gray-400">Duração:</span>
                                          <div className="font-medium text-gray-900 dark:text-white">
                                            {accessItem.duration_in_days} dias
                                          </div>
                                        </div>
                                        
                                        {accessItem.payment && (
                                          <div>
                                            <span className="text-gray-500 dark:text-gray-400">Pagamento:</span>
                                            <div className="font-medium text-gray-900 dark:text-white">
                                              {getPaymentMethodText(accessItem.payment.payment_type)}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Status adicional */}
                                      {/* {accessItem.cancelled_at ? (
                                        <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs">
                                          <span className="text-red-700 dark:text-red-300">
                                            Cancelado em {format(parseISO(accessItem.cancelled_at), "dd/MM/yyyy", { locale: ptBR })}
                                          </span>
                                        </div>
                                      ) : (
                                        <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-xs">
                                          <span className="text-blue-700 dark:text-blue-300">
                                            {getDaysRemaining(accessItem.expires_at)} dias restantes
                                          </span>
                                        </div>
                                      )} */}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      // Vista normal do plano
                      <>
                        {/* Header do Card */}
                        <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex-shrink-0 relative">

                          <div className="mb-4 pr-20">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                              {group.plan.name}
                            </h3>
                          </div>

                          <div className="absolute top-6 right-6">
                             {getStatusBadge(access)}
                          </div>

                          <div className="my-2">
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {group.plan.description}
                            </p>
                          </div>


                          {/* Preço */}
                          <div className="text-2xl font-bold text-gray-900 dark:text-white">
                            {formatPrice(access.price || group.plan.price)}
                          </div>
                        </div>

                        {/* Conteúdo */}
                        <div className="flex-1">
                          {/* Informações de Tempo */}
                          <div className="p-6 border-b border-gray-100 dark:border-gray-700">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                <CalendarIcon className="w-4 h-4 mr-2" />
                                <span className="font-medium">
                                  {format(parseISO(access.expires_at), "dd MMM yyyy", { locale: ptBR })}
                                </span>
                              </div>
                              
                              {isActive && daysRemaining > 0 && (
                                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  daysRemaining <= 7 
                                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' 
                                    : daysRemaining <= 30 
                                    ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                    : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                }`}>
                                  {daysRemaining} {daysRemaining === 1 ? 'dia' : 'dias'}
                                </div>
                              )}
                            </div>

                            {access.payment && (
                              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                <CreditCardIcon className="w-4 h-4 mr-2" />
                                <span>{getPaymentMethodText(access.payment.payment_type)}</span>
                              </div>
                            )}
                          </div>

                          {/* Cidades com Acesso */}
                          <div className="p-6 border-b border-gray-100 dark:border-gray-700">
                            <div className="flex items-center mb-3">
                              <MapPinIcon className="w-4 h-4 mr-2 text-gray-500" />
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Acesso às Cidades
                              </span>
                            </div>
                            
                            {access.allows_all_cities ? (
                              <Badge name="Todas as cidades" color="blue" />
                            ) : (
                              <div>
                                <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                  {access.number_of_cities} cidade(s) permitida(s)
                                </div>
                                {access.cities && access.cities.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {access.cities.slice(0, 3).map((city) => (
                                      <Badge key={city.id} name={city.name} color="gray" />
                                    ))}
                                    {access.cities.length > 3 && (
                                      <Badge name={`+${access.cities.length - 3} mais`} color="gray" />
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Privilégios */}
                          <div className="p-6">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                              Recursos Inclusos
                            </h4>
                            <div className="space-y-2">
                              {privileges.map((privilege, index) => (
                                <div key={index} className="flex items-center text-sm">
                                  <CheckIcon className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                                  <span className="text-gray-600 dark:text-gray-400">{privilege}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Botões de Ação */}
                        <div className="p-6 border-t border-gray-100 dark:border-gray-700 flex-shrink-0">
                          <ButtonSecondary 
                            className="w-full text-sm py-2" 
                            onClick={() => handleShowDetails(group.plan.id)}
                          >
                            Ver Histórico
                          </ButtonSecondary>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </LayoutDashboard>
    </Layout>
  );
};

export default MyPlans;
